import { makeStyles } from '@material-ui/core'
import { JsonFormsInput } from 'elements/jsonForms/JsonFormsInput'
import { FormToolsPanel } from 'elements/react-admin/FormToolsPanel'
import lodash from 'lodash'
import { ValidationErrorMessage } from 'ra-core'
import { useEffect, useState } from 'react'
import { BooleanInput, TextInput } from 'react-admin'
import { useForm, useFormState } from 'react-final-form'
import { required } from 'validations'
import { StructType, useStructTypes } from './hooks/useStructTypes'
import { StandardConditionWarnings } from './StandardConditionWarnings'
import { StructCustomUI } from './StructCustomUI'
import { StructVersionList } from './StructVersionList'
import { AdStructTypes } from './types'

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  main: { flex: 1 },
  jsonInput: {
    '& textarea': {
      maxHeight: 'calc(100vh - 535px)',
      height: 5000,
      minHeight: 300,
    },
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: 350,
    flexShrink: 0,
    paddingRight: 8,

    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      width: '100%',
    },
  },
  topFields: {
    flexWrap: 'wrap',
    display: 'flex',
    flexDirection: 'row',
    columnGap: 24,
    alignItems: 'baseline',
  },
  toggle: {
    width: 'fit-content',
  },
}))

export const StructFormContent = ({ isCreate }: { isCreate: boolean }) => {
  const structTypes = useStructTypes()
  const classes = useStyles()
  const record = useFormState().values

  const form = useForm()
  const formState = useFormState()
  const [currentType, setCurrentType] = useState<StructType | undefined>()
  const structTypeKey = formState.values.type
  const archiveSource = 'is_archived'
  const publishSource = 'is_published'
  const isCompletedSource = 'data.top_level_fields.is_completed'
  const isAdStructType = AdStructTypes.includes(structTypeKey)

  const validateToggleArchived = (isArchived: boolean, allValues): ValidationErrorMessage | undefined => {
    const isPublished = allValues.is_published

    if (isPublished && isArchived) {
      form.change('is_archived', false)
      return 'Ads cannot be Published and Archived at the same time.'
    }
    return undefined
  }

  const validateToggleCompleted = (isCompleted: boolean, allValues): ValidationErrorMessage | undefined => {
    const isPublished = allValues.is_published

    if (isCompleted && isPublished) {
      form.change('is_published', false)
    }
    return undefined
  }

  const validateTogglePublished = (isPublished: boolean, allValues): ValidationErrorMessage | undefined => {
    const isCompleted = allValues.data?.top_level_fields?.is_completed
    if (isPublished && isCompleted) {
      form.change('is_published', false)
    }

    return undefined
  }

  useEffect(() => {
    if (!structTypes || !structTypeKey) return
    setCurrentType(structTypes.find((t) => t.id === structTypeKey))
  }, [structTypeKey, structTypes])

  // When creating a new record, use the default data defined by the API
  useEffect(() => {
    const default_data = currentType?.default
    if (!isCreate || !default_data) return

    form.change('data', lodash.cloneDeep(default_data))
  }, [isCreate, currentType?.default])

  // Singular struct types use a fixed key "singular"; set it on create and keep field disabled
  useEffect(() => {
    if (isCreate && currentType?.singular) {
      form.change('key', 'singular')
    }
  }, [isCreate, currentType?.singular])

  const isSingularType = currentType?.singular === true

  return (
    <div className={classes.wrapper}>
      <div className={classes.main}>
        <div className={classes.topFields}>
          <TextInput
            source="key"
            InputLabelProps={{ shrink: true }}
            disabled={!isCreate || isSingularType}
            required={true}
            validate={required}
            format={(v) =>
              // Should match the validation in struct/models.py
              v
                ?.replace(' ', '_')
                .replace(/[^a-zA-Z0-9_-]/g, '')
                .toLowerCase()
            }
          />
          <TextInput label="Description" source="description" />
          <BooleanInput
            className={classes.toggle}
            component={BooleanInput}
            validate={[validateTogglePublished]}
            name={publishSource}
            source={publishSource}
            disabled={record.is_archived}
            label={'Published (config is active)'}
          />
          {isAdStructType && (
            <BooleanInput
              className={classes.toggle}
              component={BooleanInput}
              validate={validateToggleCompleted}
              name={isCompletedSource}
              source={isCompletedSource}
              disabled={record.is_archived}
              label={'Completed'}
            />
          )}

          <BooleanInput
            className={classes.toggle}
            component={BooleanInput}
            validate={validateToggleArchived}
            name={archiveSource}
            source={archiveSource}
            label={'Archived (config is hidden)'}
            wid
          />
        </div>
        <JsonFormsInput
          label="Data"
          source="data"
          fullWidth={true}
          validate={required}
          //@ts-ignore - prevent the textarea from being a fixed height
          rows={'unset'}
          fallbackProps={{ InputProps: { className: classes.jsonInput + ' code-block' } }}
          schema={currentType?.uischema ? currentType?.schema : undefined}
          uischema={currentType?.uischema}
        />
      </div>
      <div className={classes.rightColumn}>
        <StructCustomUI />
        <StandardConditionWarnings />
        <FormToolsPanel omitProps={['version_history']} resource={'struct_' + formState.values.type} />
        <StructVersionList
          isAdStructType={isAdStructType}
          versions={record.version_history}
          is_published={formState.initialValues.is_published}
          is_completed={formState.initialValues.is_completed}
          is_archived={formState.initialValues.is_archived}
        />
      </div>
    </div>
  )
}
